import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Loader from '../../../shared/components/common/loader/Loader';
import { checkPublicScanAccess, readScanPassword, storeScanPassword } from '../services/PublicScanService';
import './PublicScanGate.scss';

/**
 * Tymczasowa bramka hasłowa przed publicznym skanem. Hasło trzyma backend
 * (`PUBLIC_SCAN_ACCESS_PASSWORD`) — front go nie zna i tylko przekazuje to, co wpisze użytkownik.
 *
 * Nie da się tu użyć okna Basic Auth przeglądarki: pokazuje się ono przy wejściu WPROST na adres
 * API, a nie przy zapytaniach z JS. Dlatego pytamy własnym ekranem, a hasło wędruje potem
 * nagłówkiem przy każdym wywołaniu skanu.
 *
 * Gdy bramka jest wyłączona (puste hasło po stronie serwera), sprawdzenie zwraca 204 od razu i
 * użytkownik nigdy nie zobaczy tego ekranu.
 */
const PublicScanGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const [isChecking, setIsChecking] = useState(true);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Hasło z poprzedniej wizyty (sessionStorage) sprawdzamy w tle — po odświeżeniu strony nie trzeba
  // go wpisywać drugi raz, ale zamknięcie karty kasuje dostęp.
  useEffect(() => {
    let cancelled = false;

    void checkPublicScanAccess(readScanPassword()).then((ok) => {
      if (!cancelled) {
        setIsUnlocked(ok);
        setIsChecking(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const submit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setIsSubmitting(true);
      setError(false);

      const ok = await checkPublicScanAccess(password);
      if (ok) {
        storeScanPassword(password);
        setIsUnlocked(true);
      } else {
        setError(true);
      }
      setIsSubmitting(false);
    },
    [password]
  );

  if (isChecking) {
    return <Loader />;
  }

  if (isUnlocked) {
    return <>{children}</>;
  }

  return (
    <div className="public-scan-gate">
      <form className="public-scan-gate__box" onSubmit={submit}>
        <span className="ico--lock ico--lg public-scan-gate__icon" aria-hidden="true" />
        <h1 className="public-scan-gate__title">{t('publicScanGate.title')}</h1>
        <p className="public-scan-gate__desc">{t('publicScanGate.desc')}</p>

        <input
          type="password"
          className={`form-box__input${error ? ' form-box__invalid' : ''}`}
          value={password}
          autoFocus
          placeholder={t('publicScanGate.placeholder')}
          onChange={(event) => setPassword(event.target.value)}
        />
        {error && <span className="public-scan-gate__error">{t('publicScanGate.error')}</span>}

        <button type="submit" className="btn btn--sm" disabled={isSubmitting || !password}>
          {t('publicScanGate.submit')}
        </button>
      </form>
    </div>
  );
};

export default PublicScanGate;
